import React from 'react';
import * as LucideIcons from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { companyValues } from '../mock';

const Company = () => {
  const getIcon = (iconName) => {
    const Icon = LucideIcons[iconName];
    return Icon ? <Icon className="w-8 h-8" /> : null;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section - Dark Animated Background */}
      <section className="relative pt-32 pb-32 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-b from-gray-900 to-gray-800">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, #0066FF 1px, transparent 1px),
                             radial-gradient(circle at 75% 75%, #0066FF 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        <div className="absolute top-10 right-10 w-32 md:w-64 h-32 md:h-64 bg-[#0066FF]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-40 md:w-80 h-40 md:h-80 bg-[#0052CC]/20 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <Badge className="bg-white/10 text-white hover:bg-white/20 border-0 mb-4">
            About Us
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white">
            Transforming HR for the{' '}
            <span className="bg-gradient-to-r from-[#0066FF] to-[#60a5fa] bg-clip-text text-transparent">
              Modern Workplace
            </span>
          </h1>
          <p className="text-base md:text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed">
            HRMQore is on a mission to revolutionize human resource management by providing innovative, intelligent, and integrated solutions that empower businesses to focus on what matters most—their people.
          </p>
        </div>
      </section>

      {/* Our Qore Values */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#0066FF]/10 text-[#0066FF] hover:bg-[#0066FF]/20 border-0 mb-4">
              Our Qore Values
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              What Drives{' '}
              <span className="bg-gradient-to-r from-[#0066FF] to-[#0052CC] bg-clip-text text-transparent">
                Everything We Do
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our core values guide every decision, every feature, and every interaction.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {companyValues.map((value) => (
              <Card key={value.id} className="border-2 border-gray-100 hover:border-[#0066FF] hover:shadow-xl transition-all duration-300 group text-center lg:text-left">
                <CardHeader>
                  <div className="w-14 h-14 bg-gradient-to-br from-[#0066FF] to-[#0052CC] rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-200 mx-auto lg:mx-0">
                    {getIcon(value.icon)}
                  </div>
                  <CardTitle className="text-xl">{value.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Product Story - nerthbox Technologies */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-[#f0f9ff]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#0066FF]/10 text-[#0066FF] hover:bg-[#0066FF]/20 border-0 mb-4">
              A Product of nerthbox Technologies
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why{' '}
              <span className="bg-gradient-to-r from-[#0066FF] to-[#0052CC] bg-clip-text text-transparent">
                HRMQore
              </span>{' '}
              Exists
            </h2>
          </div>

          {/* The Problem */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div className="space-y-6 text-center lg:text-left">
              <div className="inline-flex items-center space-x-2 bg-red-50 text-red-600 px-4 py-2 rounded-full text-sm font-medium">
                <LucideIcons.AlertTriangle className="w-4 h-4" />
                <span>The Problem</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                HR Teams Are Drowning in Complexity
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Traditional HR management is broken. Teams waste countless hours on manual processes, struggle with disconnected systems, and lose valuable talent due to poor employee experiences.
              </p>
              <ul className="space-y-3">
                {[
                  "Spreadsheets and paper-based processes eating up productive hours",
                  "Siloed systems that don't communicate with each other",
                  "Employee frustration with outdated, clunky HR tools",
                  "Compliance risks from manual tracking and human errors",
                  "Delayed payroll and leave approvals affecting morale"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start justify-center lg:justify-start space-x-3">
                    <LucideIcons.X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-left">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://cdn.pixabay.com/photo/2014/10/01/14/07/tax-468440_1280.jpg"
                alt="HR challenges"
                className="w-full h-auto"
              />
            </div>
          </div>

          {/* The Solution */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div className="order-2 lg:order-1 relative">
              {/* Dashboard card mockup */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-200 bg-white">
                <img
                  src="https://customer-assets.emergentagent.com/job_peoplequantum/artifacts/ap9jc0vf_Screenshot%202025-12-29%20at%2004.26.47.png"
                  alt="HRMQore Dashboard"
                  className="w-full h-auto"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2 space-y-6 text-center lg:text-left">
              <div className="inline-flex items-center space-x-2 bg-green-50 text-green-600 px-4 py-2 rounded-full text-sm font-medium">
                <LucideIcons.Lightbulb className="w-4 h-4" />
                <span>The Solution</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                One Platform. Complete HR Transformation.
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                HRMQore by nerthbox Technologies brings together every HR function into a single, intelligent platform. Built by HR experts who understand your pain points, powered by technology that actually works.
              </p>
              <ul className="space-y-3">
                {[
                  "Unified platform for all HR operations—no more tool hopping",
                  "Automation that eliminates repetitive manual tasks",
                  "Employee self-service that empowers your workforce",
                  "Real-time analytics for data-driven decisions",
                  "Mobile-first design for the modern workplace"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start justify-center lg:justify-start space-x-3">
                    <LucideIcons.CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-left">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* The Impact */}
          <div className="bg-gradient-to-br from-[#0066FF] to-[#0052CC] rounded-3xl p-8 md:p-12 text-white mb-20">
            <div className="text-center mb-12">
              <div className="inline-flex items-center space-x-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
                <LucideIcons.Zap className="w-4 h-4" />
                <span>The Impact</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                Transforming the HR Ecosystem
              </h3>
              <p className="text-lg text-white/80 max-w-2xl mx-auto">
                Organizations using HRMQore are experiencing measurable improvements across every HR metric.
              </p>
            </div>
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { stat: "60%", label: "Time Saved on Admin Tasks", icon: "Clock" },
                { stat: "5x", label: "Faster Onboarding", icon: "Rocket" },
                { stat: "33%", label: "Boost in Employee Engagement", icon: "Heart" },
                { stat: "3x", label: "Faster Decision Making", icon: "Brain" }
              ].map((item, idx) => (
                <div key={idx} className="text-center">
                  <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                    {getIcon(item.icon)}
                  </div>
                  <div className="text-4xl font-bold mb-2">{item.stat}</div>
                  <div className="text-white/80 text-sm">{item.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* What You're Missing */}
          <div className="bg-gray-900 rounded-3xl p-8 md:p-12 text-white">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6 text-center lg:text-left">
                <div className="inline-flex items-center space-x-2 bg-amber-500/20 text-amber-400 px-4 py-2 rounded-full text-sm font-medium">
                  <LucideIcons.AlertCircle className="w-4 h-4" />
                  <span>Don't Get Left Behind</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold">
                  You Don't Want to Miss out
                </h3>
                <p className="text-lg text-gray-300 leading-relaxed">
                  Every day without a modern HR solution costs your organization more than you realize. Your competitors are already making the switch.
                </p>
                <ul className="space-y-4">
                  {[
                    { title: "Productivity Loss", desc: "HR teams spend 40% of their time on tasks that could be automated" },
                    { title: "Talent Attrition", desc: "Poor employee experience drives top performers to competitors" },
                    { title: "Compliance Risk", desc: "Manual processes increase errors and regulatory exposure" },
                    { title: "Competitive Disadvantage", desc: "While you're catching up, others are pulling ahead" }
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start justify-center lg:justify-start space-x-3">
                      <LucideIcons.ArrowRight className="w-5 h-5 text-amber-400 flex-shrink-0 mt-1" />
                      <div className="text-left">
                        <span className="font-semibold text-white">{item.title}:</span>
                        <span className="text-gray-300 ml-1">{item.desc}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-center">
                <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-8 inline-block">
                  <LucideIcons.TrendingDown className="w-16 h-16 text-white mx-auto mb-4" />
                  <p className="text-3xl font-bold text-white mb-2">₦2.5M+</p>
                  <p className="text-white/80 text-sm">Average annual cost of inefficient HR processes per 100 employees</p>
                </div>
                <div className="mt-8">
                  <button 
                    onClick={() => window.location.href = '/contact'}
                    className="bg-[#0066FF] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#0052CC] transition-colors duration-200"
                  >
                    Start Your Transformation
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values - Moved to second section */}

      {/* Team Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0066FF] to-[#0052CC] text-white">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Join Our Growing Team
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
            We're always looking for talented individuals who are passionate about transforming the future of work. Explore career opportunities at HRMQore.
          </p>
          <button className="bg-white text-[#0066FF] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors duration-200">
            View Open Positions
          </button>
        </div>
      </section>
    </div>
  );
};

export default Company;